import type { FC } from "react";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export interface SubagentEvent {
  subagent: string;
  toolCallId: string;
  kind: "tool_call" | "tool_result";
  toolName: string;
  summary?: string;
}

interface SubagentActivityProps {
  events: SubagentEvent[];
}

function isSuccess(summary: string | undefined): boolean {
  return !!summary && summary.startsWith("SUCCESS");
}

const TOOL_LABELS: Record<string, string> = {
  execute_code: "Executing code",
  execute_sql: "Running SQL",
};

function getStepLabel(evt: SubagentEvent): string {
  if (evt.kind === "tool_call") {
    return TOOL_LABELS[evt.toolName] ?? `Running ${evt.toolName}`;
  }
  return evt.summary?.split("\n")[0] ?? "Done";
}

export const SubagentActivity: FC<SubagentActivityProps> = ({ events }) => {
  if (events.length === 0) return null;

  return (
    <div className="mb-2 space-y-1 border-l-2 border-gray-200 pl-2.5">
      {events.map((evt, i) => (
        <div key={i} className="flex items-start gap-1.5 text-xs text-gray-500">
          <StepIcon evt={evt} />
          <span className="min-w-0 break-words leading-relaxed">{getStepLabel(evt)}</span>
        </div>
      ))}
    </div>
  );
};

const StepIcon: FC<{ evt: SubagentEvent }> = ({ evt }) => {
  if (evt.kind === "tool_call") {
    return <Loader2 className="mt-0.5 size-3 shrink-0 animate-spin text-gray-400" />;
  }
  if (isSuccess(evt.summary)) {
    return <CheckCircle2 className="mt-0.5 size-3 shrink-0 text-green-500" />;
  }
  return <XCircle className="mt-0.5 size-3 shrink-0 text-red-400" />;
};
